import { useMemo, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { Loader2 } from 'lucide-react';

const INITIAL_ROW_LIMIT = 10;

export interface AlertEvent {
  alertTypeId: number;
  customerName: string;
  category: string;
  subtype: string;
  categoryDisplayName: string;
  subtypeDisplayName: string;
  eventType: string;
  startTime: string | null;
  endTime: string | null;
  createdAt: string;
  metrics: Record<string, unknown>;
  description: string;
}

interface MeterAlertHistoryCardProps {
  activeAlerts: AlertEvent[];
  endedAlerts: AlertEvent[];
  isLoading: boolean;
  hoveredAlertIndex?: number | null;
  onAlertHover?: (index: number | null) => void;
  onAlertClick?: (alert: AlertEvent) => void;
}

function getCategoryBadgeVariant(category: string): 'destructive' | 'default' {
  return category === 'leak' || category === 'backflow' ? 'destructive' : 'default';
}

interface CustomerGroup {
  customerName: string;
  active: { alert: AlertEvent; globalIndex: number }[];
  ended: { alert: AlertEvent; globalIndex: number }[];
}

function groupByCustomer(
  activeAlerts: AlertEvent[],
  endedAlerts: AlertEvent[],
): CustomerGroup[] {
  const map = new Map<string, CustomerGroup>();

  for (let i = 0; i < activeAlerts.length; i++) {
    const alert = activeAlerts[i];
    const name = alert.customerName || 'Unknown';
    if (!map.has(name)) map.set(name, { customerName: name, active: [], ended: [] });
    map.get(name)!.active.push({ alert, globalIndex: i });
  }

  for (let i = 0; i < endedAlerts.length; i++) {
    const alert = endedAlerts[i];
    const name = alert.customerName || 'Unknown';
    if (!map.has(name)) map.set(name, { customerName: name, active: [], ended: [] });
    map.get(name)!.ended.push({ alert, globalIndex: activeAlerts.length + i });
  }

  return Array.from(map.values()).sort((a, b) => a.customerName.localeCompare(b.customerName));
}

function CustomerSection(
  { group, hoveredAlertIndex, onAlertHover, onAlertClick }: {
    group: CustomerGroup;
    hoveredAlertIndex: number | null;
    onAlertHover?: (index: number | null) => void;
    onAlertClick?: (alert: AlertEvent) => void;
  },
) {
  const [showAll, setShowAll] = useState(false);
  const activeCount = group.active.length;
  const endedCount = group.ended.length;
  const totalCount = activeCount + endedCount;

  // Always show all active rows. Trim ended rows to fill INITIAL_ROW_LIMIT.
  const endedToShow = showAll
    ? group.ended
    : group.ended.slice(0, Math.max(0, INITIAL_ROW_LIMIT - activeCount));
  const hiddenCount = group.ended.length - endedToShow.length;

  return (
    <div className='border rounded-lg'>
      <div className='flex items-center gap-2 px-4 py-3'>
        <span className='font-medium text-sm'>{group.customerName}</span>
        {activeCount > 0 && (
          <Badge variant='destructive' className='ml-1'>
            {activeCount} active
          </Badge>
        )}
        {endedCount > 0 && (
          <Badge variant='secondary' className='ml-1'>
            {endedCount} resolved
          </Badge>
        )}
      </div>

      <div className='px-4 pb-4 space-y-4'>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Category</TableHead>
              <TableHead>Subtype</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Start Time</TableHead>
              <TableHead>End Time</TableHead>
              <TableHead>Description</TableHead>
              <TableHead>Event Type</TableHead>
              <TableHead>Users Notified</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {group.active.map(({ alert, globalIndex }) => (
              <TableRow
                key={`active-${globalIndex}`}
                className={`cursor-pointer hover:bg-muted/50 ${
                  hoveredAlertIndex === globalIndex ? 'bg-muted' : ''
                }`}
                onMouseEnter={() => onAlertHover?.(globalIndex)}
                onMouseLeave={() => onAlertHover?.(null)}
                onClick={() => onAlertClick?.(alert)}
              >
                <TableCell>
                  <Badge variant={getCategoryBadgeVariant(alert.category)}>
                    {alert.categoryDisplayName}
                  </Badge>
                </TableCell>
                <TableCell>{alert.subtypeDisplayName}</TableCell>
                <TableCell>
                  <Badge variant='destructive'>active</Badge>
                </TableCell>
                <TableCell>
                  {alert.startTime
                    ? new Date(alert.startTime).toLocaleString()
                    : <span>&mdash;</span>}
                </TableCell>
                <TableCell>&mdash;</TableCell>
                <TableCell>
                  <span title={alert.description}>
                    {alert.description.length > 80
                      ? `${alert.description.slice(0, 80)}…`
                      : alert.description}
                  </span>
                </TableCell>
                <TableCell>
                  <Badge variant='outline'>{alert.eventType}</Badge>
                </TableCell>
                <TableCell>0</TableCell>
              </TableRow>
            ))}
            {endedToShow.map(({ alert, globalIndex }) => (
              <TableRow
                key={`ended-${globalIndex}`}
                className={`cursor-pointer hover:bg-muted/50 ${
                  hoveredAlertIndex === globalIndex ? 'bg-muted' : ''
                }`}
                onMouseEnter={() => onAlertHover?.(globalIndex)}
                onMouseLeave={() => onAlertHover?.(null)}
                onClick={() => onAlertClick?.(alert)}
              >
                <TableCell>
                  <Badge variant={getCategoryBadgeVariant(alert.category)}>
                    {alert.categoryDisplayName}
                  </Badge>
                </TableCell>
                <TableCell>{alert.subtypeDisplayName}</TableCell>
                <TableCell>
                  <Badge variant='secondary'>ended</Badge>
                </TableCell>
                <TableCell>
                  {alert.startTime
                    ? new Date(alert.startTime).toLocaleString()
                    : <span>&mdash;</span>}
                </TableCell>
                <TableCell>
                  {alert.endTime ? new Date(alert.endTime).toLocaleString() : <span>&mdash;</span>}
                </TableCell>
                <TableCell>
                  <span title={alert.description}>
                    {alert.description.length > 80
                      ? `${alert.description.slice(0, 80)}…`
                      : alert.description}
                  </span>
                </TableCell>
                <TableCell>
                  <Badge variant='outline'>{alert.eventType}</Badge>
                </TableCell>
                <TableCell>0</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        {hiddenCount > 0 && (
          <div className='flex justify-center'>
            <Button
              variant='ghost'
              size='sm'
              onClick={() => setShowAll(true)}
            >
              Show {hiddenCount} more ({totalCount} total)
            </Button>
          </div>
        )}
        {showAll && endedCount > INITIAL_ROW_LIMIT - activeCount && (
          <div className='flex justify-center'>
            <Button
              variant='ghost'
              size='sm'
              onClick={() => setShowAll(false)}
            >
              Show less
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}

export function MeterAlertHistoryCard(
  {
    activeAlerts,
    endedAlerts,
    isLoading,
    hoveredAlertIndex = null,
    onAlertHover,
    onAlertClick,
  }: MeterAlertHistoryCardProps,
) {
  const groups = useMemo(
    () => groupByCustomer(activeAlerts, endedAlerts),
    [activeAlerts, endedAlerts],
  );

  if (isLoading) {
    return (
      <Card>
        <CardContent className='flex items-center justify-center py-8'>
          <Loader2 className='h-6 w-6 animate-spin text-muted-foreground' />
          <span className='ml-2 text-sm text-muted-foreground'>Loading alert history...</span>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader className='pb-3'>
        <CardTitle className='text-lg'>Alert History by customer</CardTitle>
      </CardHeader>
      <CardContent>
        {groups.length === 0
          ? (
            <p className='text-sm text-muted-foreground'>
              No alert history for this sensor
            </p>
          )
          : (
            <div className='space-y-3'>
              {groups.map((group) => (
                <CustomerSection
                  key={group.customerName}
                  group={group}
                  hoveredAlertIndex={hoveredAlertIndex}
                  onAlertHover={onAlertHover}
                  onAlertClick={onAlertClick}
                />
              ))}
            </div>
          )}
      </CardContent>
    </Card>
  );
}
